import { Input } from "components/Inputs";
import { Breadcrumbs, Button, Header, LoadingState, View, Workspace } from "components/Shared";
import { translate } from "lib";
import Datasource, { DatasourceResponse } from "lib/datasource";
import _ from "lodash";
import moment from "moment";
import React, { memo, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { cleanDate, cleanNumber, cleanText, dash } from "./formHelpers";
import { useFeatureFlagGuard } from "v2/hooks";

import { baseText } from "./StatedBase";
/**
 * Correct one amendment (GET / PATCH /v2/amendments/:id).
 *
 * ITS OWN PAGE, reached by the Edit button at the bottom of AmendmentShow's
 * Details tab — the same relationship LeaseAgreementEdit has with the lease's
 * Details tab, and for the same reason (Sim, 26 Aug): a detail screen STATES the
 * record, and correcting it is somewhere you choose to go. The form briefly lived
 * inside the Details tab itself, which made every visit to the amendment a visit
 * to a form.
 *
 * On save it returns to the amendment's Details tab, not to the lease's
 * Amendments list: that is the screen the Edit button was pressed on, and it is
 * where the corrected values are read back.
 *
 * ---------------------------------------------------------------------------
 * TWO GROUPS ARE WRITABLE. FOUR FIELDS ARE SHOWN AND LOCKED.
 * ---------------------------------------------------------------------------
 * Yardi owns amendments; this page exists so a human can fix data that arrived
 * wrong, not to author one.
 *
 *   the amendment's own    `amendment_start_date`, `amendment_end_date`,
 *                          `amendment_status` — its window and where it is up to.
 *   what it says the       contracted area, term length, unit status, unit of
 *   lease is               measure, handover / opening / contraction / move-out
 *                          dates, fitout, sales category, RDD activity, LRTF
 *                          notes. THESE MOVED HERE FROM THE LEASE (Sim, 24 Aug):
 *                          each is a statement about a term, and one copy per
 *                          lease meant the newest payload won and what the
 *                          previous term was agreed on was gone. They were
 *                          correctable on the lease form, so they stay
 *                          correctable — same fields, one screen along.
 *
 * V2::AmendmentsController#safe_params documents why each of the four locked
 * ones is out: `amendment_id` is half the dedup key the next payload matches on,
 * `amendment_type` decides what the amendment DOES (and already picked the
 * template and workflow), `base_amendment_id` is the tree link that the unit
 * fold walks, and cancellation is an amendment arriving rather than a column
 * write. Sending any of them would be dropped by `permit` anyway — they are
 * rendered locked so it is obvious why the inputs are missing.
 *
 * ASSETS ARE NOWHERE ON THIS FORM AND ARE NOT WRITABLE ANYWHERE. They belong to
 * the amendment (`assets.amendment_id`, Sim 26 Aug) but only /lease_data writes
 * them — no `Asset` in `safe_params`, no endpoint to send one to. They are read
 * on AmendmentShow's Assets tab.
 *
 * A later /lease_data payload can still overwrite every writable field. This is
 * a correction, not a lock.
 */

/**
 * `src/components/Inputs/Input.jsx` runs `moment(value).format("YYYY-MM-DD")` on
 * every change of a date input, so merely FOCUSING an empty one writes the
 * literal string "Invalid date" into form state (see the note on
 * Leases/Form.tsx). Sent as-is that is a 422 at best and a garbage date at
 * worst, so every date is normalised on the way out: a real date stays, and
 * anything else — "Invalid date", "" — becomes null, which is also how a user
 * CLEARS a date.
 */
// Exactly what V2::AmendmentsController#safe_params permits, grouped by how each
// value has to be cleaned on the way out. Nothing else is seeded into form
// state, so nothing else can be sent by accident.
const TEXT_FIELDS = [
	"amendment_status",
	"unit_status",
	"unit_of_measure",
	"fitout_type",
	"sales_category",
	"rdd_activity_description",
	"lrtf_notes"
];
const NUMBER_FIELDS = ["contracted_area", "term", "fitout_term"];
const DATE_FIELDS = [
	"amendment_start_date",
	"amendment_end_date",
	"move_in_date",
	"opening_at",
	"contraction_date",
	"move_out_date"
];
const WRITABLE = [...TEXT_FIELDS, ...NUMBER_FIELDS, ...DATE_FIELDS];

const AmendmentEdit = () => {
	const history = useHistory();
	const { id, amendmentId } = useParams<{ id: string; amendmentId: string }>();
	// null = still loading; false = the API refused, or the record is gone.
	const [amendment, setAmendment] = useState<tcpinpoint.Amendment | null | false>(null);
	const [data, setData] = useState<any>({});
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const [datasource] = useState(new Datasource({ mainModelName: "amendment" }));

	useEffect(() => {
		let sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) => {
			// The same subject carries the GET and the PATCH answer. Only the GET
			// seeds the form — re-seeding after a save would fight the redirect.
			if (!response.success) {
				setAmendment(previous => (previous === null ? false : previous));
				return;
			}
			const record = response.normalizedMainModelResponse;
			setAmendment(previous => (previous === null ? record || false : previous));
			setData((previous: any) => (_.isEmpty(previous) ? _.pick(record, WRITABLE) : previous));
		});
		return () => sub.unsubscribe();
	}, [datasource]);

	useEffect(() => {
		if (!_.isEmpty(amendmentId)) datasource.get("v2", `amendments/${amendmentId}`, {});
	}, [datasource, amendmentId]);

	// BACK TO THE AMENDMENT, NOT TO THE LEASE. `#details` is where the Edit button
	// was, and where the values just saved are read back.
	const backPath = `/lease-agreements/${id}/amendments/${amendmentId}#details`;

	const onChange = (field: string) => (value: any) => setData(_.assign({}, data, { [field]: value }));

	const onSubmit = () => {
		const startsAt = cleanDate(data.amendment_start_date);
		const endsAt = cleanDate(data.amendment_end_date);

		// An end date before the start date makes the node permanently `expired?`,
		// which silently drops every unit it introduces out of Amendment::UnitFold.
		// Caught here rather than discovered as a lease that lost its units.
		if (!!startsAt && !!endsAt && moment(endsAt).isBefore(moment(startsAt))) {
			setError(translate("amendment_end_before_start") as string);
			return;
		}

		setError(null);
		setSaving(true);

		const payload: any = {};
		_.forEach(TEXT_FIELDS, field => (payload[field] = cleanText(data[field])));
		_.forEach(NUMBER_FIELDS, field => (payload[field] = cleanNumber(data[field])));
		_.forEach(DATE_FIELDS, field => (payload[field] = cleanDate(data[field])));
		// Already cleaned above for the comparison — reused rather than re-parsed.
		payload.amendment_start_date = startsAt;
		payload.amendment_end_date = endsAt;

		datasource
			.put("v2", `amendments/${amendmentId}`, { body: { amendment: payload } })
			.then((response: any) => {
				setSaving(false);
				// `put` resolves on failure too (Datasource#call catches and pushes a
				// failed response), so only navigate when something came back.
				if (!!_.get(response, "normalizedMainModelResponse")) history.push(backPath);
			});
	};

	// BEHIND THE `lease-agreements` FLAG (Sim, 22 Sep 2026) — see the note on the
	// Show screens. Correcting Yardi data needs the section; a user without the
	// flag goes to the dashboard rather than seeing the 403's not-found state.
	const allowed = useFeatureFlagGuard("lease-agreements");
	if (!allowed) return null;

	if (amendment === null) return <LoadingState />;

	if (amendment === false)
		return (
			<React.Fragment>
				<Breadcrumbs items={[{ title: "lease_agreements", link: "/lease-agreements" }]} />
				<Header title="amendment" stamp="lease_agreements" />
				<Workspace id="amendment-edit">
					<View resource="amendment">
						<div className="alert alert-light empty-state">{translate("amendment_not_found")}</div>
					</View>
				</Workspace>
			</React.Fragment>
		);

	const leaseTitle = amendment.lease_agreement_tcode || (translate("lease_agreement") as string);
	const title = `${amendment.amendment_type || translate("amendment")} #${amendment.amendment_id}`;

	return (
		<React.Fragment>
			<Breadcrumbs
				items={[
					{ title: "lease_agreements", link: "/lease-agreements" },
					{ title: leaseTitle, link: `/lease-agreements/${id}` },
					{ title, link: `/lease-agreements/${id}/amendments/${amendmentId}` },
					{ title: "amendment_edit" }
				]}
			/>
			<Header title={title} subtitle="amendment_edit" stamp="lease_agreements" />
			<Workspace id="amendment-edit">
				<View resource="amendment" title="amendment_edit">
					{!!error && (
						<div className="alert alert-danger" role="alert">
							{error}
						</div>
					)}

					{/* LOCKED FIELDS FIRST (Sim, 24 Aug), so the page reads as "here is
					    the amendment, and here is the part of it you may correct" — the
					    same order as the lease form. Their order within the group is Sim's
					    (25 Aug): what the amendment IS, then whether it still stands, then
					    the two ids.
					    `disabled` rather than components/Inputs/Static: Input's disabled
					    branch renders the value as `.form-control-static` inside the same
					    label and form-group, so a locked field sits flush with the writable
					    ones below — and Static is not exported from the Inputs barrel.
					    `is-locked` is what greys it: _form.scss already carries that rule
					    (the Template's Property field uses it), at the specificity needed to
					    beat the `.form-control` mixin's transparent background. Why each of
					    these is locked is in the file header. */}
					<Input name="amendment_type" defaultState={amendment} disabled className="is-locked" formatter={dash} />
					<Input
						name="cancelled"
						defaultState={amendment}
						disabled
						className="is-locked"
						formatter={(value: any) => (!!value ? translate("yes") : translate("no"))}
					/>
					<Input name="amendment_id" defaultState={amendment} disabled className="is-locked" formatter={dash} />
					{/* Yardi's own BaseAmendmentID, then the resolved parent — same as the
					    Details tab, as plain text because an input cannot style half its
					    value (see StatedBase). */}
					<Input
						name="base_amendment_id"
						defaultState={amendment}
						disabled
						className="is-locked"
						formatter={() => dash(baseText(amendment))}
					/>

					<hr style={{ margin: "1.5rem 0" }} />

					{/* WRITABLE, in the order Sim asked for (25 Aug): status, the amendment's
					    own window, the remaining dates, then the term / fitout / area fields.
					    The amendment's own fields and the ones that moved off the lease form
					    (Sim, 24 Aug) no longer sit either side of a rule — that order asks for
					    them interleaved, so there is one writable block.
					    `type="date"` is passed explicitly: Input's own date detection keys on
					    `_at` in the render path but on `_date` in the change path, so without
					    it these would render as text boxes whose values were still
					    moment-formatted on every keystroke. */}
					<Input name="amendment_status" defaultState={data} onChange={onChange("amendment_status")} />
					<Input
						name="amendment_start_date"
						type="date"
						defaultState={data}
						onChange={onChange("amendment_start_date")}
					/>
					<Input
						name="amendment_end_date"
						type="date"
						defaultState={data}
						onChange={onChange("amendment_end_date")}
					/>
					<Input name="move_in_date" type="date" defaultState={data} onChange={onChange("move_in_date")} />
					<Input name="move_out_date" type="date" defaultState={data} onChange={onChange("move_out_date")} />
					<Input
						name="contraction_date"
						type="date"
						defaultState={data}
						onChange={onChange("contraction_date")}
					/>
					{/* An `_at` name, detected as a date on both of Input's paths, so it
					    needs no explicit `type` — unlike the `_date` names around it.
					    Labelled "Opening Date (Expected Open for Trade Date)" (Sim, 28 Oct
					    2026), the same key the Details tab uses, through `label` rather than
					    by rewriting the shared `opening_at` entry, which the lease form and
					    the project cards still want as "Expected Opening Date (Opening
					    Date)". */}
					<Input
						name="opening_at"
						label="expected_open_for_trade_date"
						defaultState={data}
						onChange={onChange("opening_at")}
					/>
					{/* The term LENGTH in months. NOT the window above — Yardi states both
					    and they can disagree; the window is what the unit fold reads. */}
					<Input name="term" type="number" defaultState={data} onChange={onChange("term")} />
					<Input name="fitout_term" type="number" defaultState={data} onChange={onChange("fitout_term")} />
					<Input name="fitout_type" defaultState={data} onChange={onChange("fitout_type")} />
					{/* `contracted_area` ends in `_area`, so Input already gives it a number
					    box and an m² suffix of its own — hence no `type` here. */}
					<Input name="contracted_area" defaultState={data} onChange={onChange("contracted_area")} />
					<Input name="unit_of_measure" defaultState={data} onChange={onChange("unit_of_measure")} />
					<Input name="unit_status" defaultState={data} onChange={onChange("unit_status")} />
					<Input name="sales_category" defaultState={data} onChange={onChange("sales_category")} />
					<Input
						name="rdd_activity_description"
						defaultState={data}
						onChange={onChange("rdd_activity_description")}
					/>
					<Input name="lrtf_notes" defaultState={data} onChange={onChange("lrtf_notes")} />

					<Button onClick={onSubmit} loading={saving} disabled={saving}>
						{translate("save")}
					</Button>
				</View>
			</Workspace>
		</React.Fragment>
	);
};

export default memo(AmendmentEdit);
